import type { ChecklistItemType, ChecklistSchedule, ChecklistTemplate } from '@up2d8/contracts';

/**
 * Wat de checklistbouwer in het beheer invult, en wat daarvan naar de API gaat (hertest 29 okt.).
 *
 * Apart van het scherm zodat de twee beslissingen die erin zitten te testen zijn: een item is
 * verplicht tenzij de beheerder het uitzet, en een planning gaat altijd volledig mee — zodat wat
 * op het scherm staat ook is wat er bewaard wordt.
 */
export interface DraftItem {
  key: number;
  label: string;
  type: ChecklistItemType;
  unit: string;
  min: string;
  max: string;
  /** Standaard aan, zoals bij een formulierveld; uit = de ronde kan zonder dit item af. */
  required: boolean;
}

let nextKey = 1;
export function emptyItem(): DraftItem {
  nextKey += 1;
  return { key: nextKey, label: '', type: 'check', unit: '', min: '', max: '', required: true };
}

/**
 * Het item zoals de API het verwacht. `required` stond hier vast op `true`, terwijl de API en de
 * app optionele items al kenden: elk item in het beheer was dus verplicht.
 */
export function itemBody(item: DraftItem) {
  return {
    label: item.label,
    help: '',
    type: item.type,
    required: item.required,
    min: item.type === 'number' && item.min !== '' ? Number(item.min) : null,
    max: item.type === 'number' && item.max !== '' ? Number(item.max) : null,
    unit: item.type === 'number' ? item.unit : '',
  };
}

/** De planning zoals de velden ze tonen: tekst, want het zijn invoervelden. */
export interface PlanningDraft {
  schedule: ChecklistSchedule;
  planTime: string;
  weekday: string;
  dueHours: string;
}

export const NEW_PLANNING: PlanningDraft = {
  schedule: 'daily',
  planTime: '06:00',
  weekday: '1',
  dueHours: '8',
};

/** De velden bij "Bewerken": wat er bewaard is, en niet de standaard van een nieuwe checklist. */
export function planningOf(
  template: Pick<ChecklistTemplate, 'schedule' | 'planTime' | 'weekday' | 'dueHours'>,
): PlanningDraft {
  return {
    schedule: template.schedule,
    planTime: template.planTime,
    weekday: String(template.weekday ?? 1),
    dueHours: String(template.dueHours),
  };
}

/** De planning voor `POST`/`PATCH /checklists/templates`. */
export function planningBody(draft: PlanningDraft) {
  return {
    schedule: draft.schedule,
    planTime: draft.planTime,
    weekday: draft.schedule === 'weekly' ? Number(draft.weekday) : null,
    dueHours: Number(draft.dueHours) || 8,
  };
}

/** Staat er op het scherm iets anders dan wat bewaard is? Dan pas is "Planning bewaren" zinvol. */
export function planningChanged(
  template: Pick<ChecklistTemplate, 'schedule' | 'planTime' | 'weekday' | 'dueHours'>,
  draft: PlanningDraft,
): boolean {
  const body = planningBody(draft);
  return (
    body.schedule !== template.schedule ||
    body.planTime !== template.planTime ||
    body.weekday !== (template.schedule === 'weekly' ? template.weekday : null) ||
    body.dueHours !== template.dueHours
  );
}
